import {Component} from '@packages/systems/wfdl';
import {getMeasurableNativeNodeByNativeIdPath} from '@packages/systems/designer/canvas';
import {
  getNodeNativeIdPath,
  getClosestNodeNativeIdPath,
} from '@packages/shared/utilities/ElementDomUtils';
import {getOutlinePositionMode} from '@packages/systems/designer/ui';
import {getBounds} from '@packages/shared/utilities/DomUtils';
import {
  isPathContainingInstances,
  getPathToOutermostSymbolRoot,
} from '@packages/utilities/platform';

/**
 * Retrieves the native ID path, bounds, and position mode for a given target element.
 * If the native ID path contains instances and components are provided, it resolves
 * to the outermost symbol's root node and updates the bounds accordingly.
 *
 * @param targetElement - The target HTML element to analyze.
 * @param components - The component map, used to resolve paths containing instances.
 * @returns An object containing the native ID path, and bounds
 */
export const resolveNodePathAndBounds = (
  targetElement: HTMLElement,
  components: Component.ComponentMap | undefined
) => {
  // Retrieve initial native ID path and bounds
  let nativeIdPath = getNodeNativeIdPath(targetElement);
  let bounds = getBounds(targetElement);

  // On canvas app user can interact (hover and click) with the elements that are generated via custom code
  // These elements don't have any webflow related attributes carried on them
  // and can not become selected/hovered etc on their own. For this reason we'll look up for the closest node that has "data-wf-id" attribute
  if (nativeIdPath.length === 0) {
    nativeIdPath = getClosestNodeNativeIdPath(targetElement);
    const nativeNode = getMeasurableNativeNodeByNativeIdPath(nativeIdPath);
    if (nativeNode) {
      const updatedBounds = getBounds(nativeNode);
      bounds = updatedBounds;
    }
  }

  // If the path contains instances and components are provided, resolve to the outermost symbol
  if (isPathContainingInstances(nativeIdPath) && components) {
    const outermostSymbolPath = getPathToOutermostSymbolRoot(
      nativeIdPath,
      components
    );

    if (outermostSymbolPath) {
      const nativeNode =
        getMeasurableNativeNodeByNativeIdPath(outermostSymbolPath);

      if (nativeNode) {
        // Update bounds and position if the outermost symbol path is resolved
        const updatedBounds = getBounds(nativeNode);
        const updatedPosition = getOutlinePositionMode(nativeNode);

        if (updatedBounds && updatedPosition) {
          nativeIdPath = outermostSymbolPath;
          bounds = updatedBounds;
        }
      }
    }
  }

  return {nativeIdPath, bounds};
};
